<template>
  <div id="menu">
    <h1>⚔️ THE ULTIMATE WAR ⚔️</h1>
    <p class="version">v{{ gameVersion }}</p>
    <p class="subtitle">Kies je ras:</p>
    
    <div class="difficulty-select" v-if="selectedRace">
      <p class="subtitle-small">Moeilijkheidsgraad:</p>
      <div class="difficulty-buttons">
        <button 
          v-for="difficulty in availableDifficulties" 
          :key="difficulty.id"
          :class="['difficulty-button', difficulty.id, { selected: selectedDifficulty === difficulty.id, locked: !difficulty.unlocked }]"
          @click="selectDifficulty(difficulty.id)"
          :disabled="!difficulty.unlocked"
          type="button"
        >
          {{ difficulty.emoji }} {{ difficulty.name }}
          <span v-if="!difficulty.unlocked" class="lock-icon">🔒</span>
        </button>
      </div>
    </div>
    
    <div class="race-select">
      <button 
        v-for="race in availableRaces" 
        :key="race.id"
        :class="['race-button', race.id, { selected: selectedRace === race.id, locked: !race.unlocked }]"
        @click="selectRace(race.id)"
        :disabled="!race.unlocked"
        type="button"
      >
        {{ race.emoji }} {{ race.name }}
        <span v-if="!race.unlocked" class="lock-icon">🔒</span>
      </button>
    </div>
    
    <div class="game-modes">
      <button 
        class="game-button tutorial-button" 
        @click.stop="startTutorial"
        type="button"
      >
        📚 Tutorial
      </button>
      <button 
        class="game-button" 
        :class="{ disabled: !selectedRace }"
        @click="handleSinglePlayerClick"
        type="button"
        id="single-player-btn"
      >
        Single Player
      </button>
      <button 
        class="game-button" 
        :disabled="!selectedRace"
        @click.stop.prevent="startGame('coop', $event)"
        @mousedown.stop
        type="button"
      >
        Co-op (2-4 spelers)
      </button>
    </div>
    
    <p class="warning">
      ⚠️ 18+ - Bevat geweld en bloed
    </p>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, computed } from 'vue';
import { GAME_VERSION } from '../../version.js';

const emit = defineEmits(['start-game', 'start-tutorial']);

const gameVersion = ref(GAME_VERSION);

const selectedRace = ref(null);
const selectedDifficulty = ref('medium'); // Default to medium

// Check if game has been won (unlocks)
const gameWon = ref(false);
const wizardUnlocked = ref(false);
const impossibleUnlocked = ref(false);

const checkUnlocks = () => {
  gameWon.value = localStorage.getItem('gameWon') === 'true';
  wizardUnlocked.value = localStorage.getItem('wizardUnlocked') === 'true';
  impossibleUnlocked.value = localStorage.getItem('impossibleUnlocked') === 'true';
};

onMounted(() => {
  // Check localStorage for unlocks
  checkUnlocks();
  
  // Listen for storage changes (when game is won in another tab/window)
  window.addEventListener('storage', checkUnlocks);
  
  // Listen for custom event when unlocks are updated
  window.addEventListener('gameUnlocksUpdated', checkUnlocks);
  
  // Debug: Log button state
  setTimeout(() => {
    const btn = document.getElementById('single-player-btn');
    if (btn) {
      console.log('Single player button found:', btn);
      console.log('Button disabled?', btn.disabled);
      console.log('Button classes:', btn.className);
      console.log('selectedRace.value:', selectedRace.value);
    } else {
      console.warn('Single player button not found!');
    }
  }, 500);
});

// Also check unlocks when component becomes visible again
onActivated(() => {
  checkUnlocks();
});

const races = [
  { id: 'human', name: 'MENSEN', emoji: '👤', color: '#4169E1', unlocked: true },
  { id: 'orc', name: 'ORKS', emoji: '👹', color: '#228B22', unlocked: true },
  { id: 'elf', name: 'ELVEN', emoji: '🧝', color: '#FFD700', unlocked: true },
  { id: 'troll', name: 'TROLLEN', emoji: '🧌', color: '#8B4513', unlocked: true },
  { id: 'wizard', name: 'WIZARD (SECRET)', emoji: '🧙', color: '#9D4EDD', unlocked: false }
];

const availableRaces = computed(() => {
  return races.map(race => ({
    ...race,
    unlocked: race.id === 'wizard' ? wizardUnlocked.value : true
  }));
});

const difficulties = [
  { id: 'easy', name: 'MAKKELIJK', emoji: '🟢', unlocked: true },
  { id: 'medium', name: 'NORMALE', emoji: '🟡', unlocked: true },
  { id: 'hard', name: 'MOEILIJK', emoji: '🔴', unlocked: true },
  { id: 'impossible', name: 'IMPOSSIBLE', emoji: '💀', unlocked: false }
];

const availableDifficulties = computed(() => {
  return difficulties.map(diff => ({
    ...diff,
    unlocked: diff.id === 'impossible' ? impossibleUnlocked.value : true
  }));
});

const selectRace = (raceId) => {
  const race = races.find(r => r.id === raceId);
  if (race && race.id === 'wizard' && !wizardUnlocked.value) {
    alert('Je moet eerst het spel winnen om de Wizard te unlocken!');
    return;
  }
  console.log('Race selected:', raceId);
  selectedRace.value = raceId;
  console.log('selectedRace.value is now:', selectedRace.value);
};

const selectDifficulty = (difficultyId) => {
  const difficulty = difficulties.find(d => d.id === difficultyId);
  if (difficulty && difficulty.id === 'impossible' && !impossibleUnlocked.value) {
    alert('Je moet eerst het spel winnen om Impossible te unlocken!');
    return;
  }
  selectedDifficulty.value = difficultyId;
};

const handleSinglePlayerClick = (event) => {
  console.log('=== handleSinglePlayerClick CALLED ===');
  console.log('selectedRace.value:', selectedRace.value);
  console.log('selectedDifficulty.value:', selectedDifficulty.value);
  
  if (!selectedRace.value) {
    alert('Selecteer eerst een ras!');
    return;
  }
  
  console.log('Calling startGame...');
  startGame('single', event);
};

const startGame = (mode, event) => {
  console.log('=== startGame CALLED ===');
  console.log('Mode:', mode);
  console.log('selectedRace.value:', selectedRace.value);
  console.log('selectedDifficulty.value:', selectedDifficulty.value);
  
  // Check if race is selected
  if (!selectedRace.value) {
    console.warn('No race selected!');
    alert('Selecteer eerst een ras!');
    return;
  }
  
  console.log('Emitting start-game event...');
  console.log('Event details:', {
    race: selectedRace.value,
    mode: mode,
    difficulty: selectedDifficulty.value
  });
  
  try {
    emit('start-game', selectedRace.value, mode, selectedDifficulty.value);
    console.log('✅ Event emitted successfully');
  } catch (error) {
    console.error('❌ Error emitting event:', error);
    alert('Er is een fout opgetreden: ' + error.message);
  }
};

const startTutorial = () => {
  emit('start-tutorial');
};
</script>

<style scoped>
#menu {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: all !important;
  background: rgba(0, 0, 0, 0.9);
  padding: 40px;
  border-radius: 10px;
  border: 2px solid #8B0000;
  box-shadow: 0 0 30px rgba(139, 0, 0, 0.5);
  min-width: 400px;
  z-index: 200;
}

#menu h1 {
  color: #8B0000;
  margin-bottom: 20px;
  font-size: 2.5em;
  text-shadow: 2px 2px 4px #000;
}

.version {
  margin: -10px 0 10px 0;
  color: #666;
  font-size: 0.9em;
  font-weight: normal;
  font-style: italic;
}

.subtitle {
  margin-bottom: 20px;
  color: #888;
  font-size: 1.1em;
}

.subtitle-small {
  margin-bottom: 10px;
  color: #aaa;
  font-size: 0.95em;
}

.difficulty-select {
  margin: 20px 0;
}

.difficulty-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
}

.difficulty-button {
  padding: 12px 20px;
  background: #1a1a1a;
  border: 2px solid #666;
  color: #fff;
  cursor: pointer;
  border-radius: 5px;
  font-size: 1em;
  transition: all 0.3s;
  font-weight: bold;
  flex: 1;
}

.difficulty-button:hover {
  transform: scale(1.05);
  box-shadow: 0 0 10px rgba(139, 0, 0, 0.5);
}

.difficulty-button.selected {
  border-width: 3px;
  box-shadow: 0 0 15px currentColor;
}

.difficulty-button.easy {
  border-color: #00ff00;
}

.difficulty-button.easy:hover,
.difficulty-button.easy.selected {
  background: rgba(0, 255, 0, 0.1);
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
}

.difficulty-button.medium {
  border-color: #ffaa00;
}

.difficulty-button.medium:hover,
.difficulty-button.medium.selected {
  background: rgba(255, 170, 0, 0.1);
  box-shadow: 0 0 15px rgba(255, 170, 0, 0.5);
}

.difficulty-button.hard {
  border-color: #ff0000;
}

.difficulty-button.hard:hover,
.difficulty-button.hard.selected {
  background: rgba(255, 0, 0, 0.1);
  box-shadow: 0 0 15px rgba(255, 0, 0, 0.5);
}

.race-select {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin: 30px 0;
}

.race-button {
  padding: 20px;
  background: #1a1a1a;
  border: 2px solid #444;
  color: #fff;
  cursor: pointer;
  border-radius: 5px;
  font-size: 1.2em;
  transition: all 0.3s;
  font-weight: bold;
}

.race-button:hover {
  transform: scale(1.05);
  box-shadow: 0 0 15px rgba(139, 0, 0, 0.5);
}

.race-button.selected {
  border-width: 4px;
  box-shadow: 0 0 20px currentColor;
}

.race-button.human { 
  border-color: #4169E1; 
}

.race-button.human:hover,
.race-button.human.selected {
  background: rgba(65, 105, 225, 0.2);
}

.race-button.orc { 
  border-color: #228B22; 
}

.race-button.orc:hover,
.race-button.orc.selected {
  background: rgba(34, 139, 34, 0.2);
}

.race-button.elf { 
  border-color: #FFD700; 
}

.race-button.elf:hover,
.race-button.elf.selected {
  background: rgba(255, 215, 0, 0.2);
}

.race-button.troll { 
  border-color: #8B4513; 
}

.race-button.troll:hover,
.race-button.troll.selected {
  background: rgba(139, 69, 19, 0.2);
}

.game-modes {
  margin: 30px 0;
}

.game-button {
  padding: 15px 30px;
  margin: 10px;
  background: #8B0000;
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 5px;
  font-size: 1.1em;
  font-weight: bold;
  transition: all 0.3s;
  position: relative;
  z-index: 10;
  pointer-events: auto !important;
}

.game-button:hover:not(:disabled) {
  background: #A00000;
  transform: scale(1.05);
  box-shadow: 0 0 15px rgba(139, 0, 0, 0.7);
}

.game-button:disabled,
.game-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.tutorial-button {
  background: #ffaa00 !important;
  color: #000 !important;
}

.tutorial-button:hover:not(:disabled) {
  background: #ffcc00 !important;
  box-shadow: 0 0 15px rgba(255, 170, 0, 0.7) !important;
}

.warning {
  margin-top: 20px;
  color: #666;
  font-size: 0.9em;
}

.race-button.locked,
.difficulty-button.locked {
  opacity: 0.5;
  cursor: not-allowed;
  position: relative;
}

.race-button:disabled,
.difficulty-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lock-icon {
  margin-left: 5px;
  font-size: 0.8em;
}

/* Tablet responsive styles */
@media (max-width: 1024px) {
  #menu {
    min-width: 90%;
    max-width: 600px;
    padding: 30px 20px;
  }
  
  #menu h1 {
    font-size: 2em;
  }
  
  .race-select {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .race-button {
    padding: 15px;
    font-size: 1em;
  }
  
  .difficulty-buttons {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .difficulty-button {
    padding: 10px 15px;
    font-size: 0.9em;
    min-width: 100px;
  }
  
  .game-modes {
    flex-direction: column;
    gap: 10px;
  }
  
  .game-button {
    padding: 15px;
    font-size: 1em;
  }
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  #menu {
    min-width: 95%;
    padding: 20px 15px;
  }
  
  #menu h1 {
    font-size: 1.5em;
    margin-bottom: 15px;
  }
  
  .race-select {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .race-button {
    padding: 12px;
    font-size: 0.9em;
  }
  
  .difficulty-buttons {
    flex-direction: column;
  }
  
  .difficulty-button {
    width: 100%;
  }
}
</style>

